import React, { useState, useEffect } from 'react';
import axios from '../config/axios';
import { useTranslation } from '../hooks/useTranslation';

const AdminManagement = () => {
  const { t } = useTranslation();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    role: 'editor'
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get('/cms/admins');
      setAdmins(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching admins:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAdmin) {
        await axios.put(`/cms/admins/${editingAdmin.id}`, formData);
      } else {
        await axios.post('/cms/admins', formData);
      }
      fetchAdmins();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving admin:', error);
      alert(error.response?.data?.error || 'Error saving admin');
    }
  };

  const handleDelete = async (id, username) => {
    if (!window.confirm(`Are you sure you want to delete admin "${username}"?`)) {
      return;
    }
    try {
      await axios.delete(`/cms/admins/${id}`);
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert(error.response?.data?.error || 'Error deleting admin');
    }
  };

  const handleToggleActive = async (id, isActive) => {
    try {
      await axios.patch(`/cms/admins/${id}/toggle-active`, { isActive: !isActive });
      fetchAdmins();
    } catch (error) {
      console.error('Error toggling admin status:', error);
      alert(error.response?.data?.error || 'Error updating admin status');
    }
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      username: admin.username,
      email: admin.email,
      password: '',
      fullName: admin.fullName || '',
      role: admin.role
    });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingAdmin(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      fullName: '',
      role: 'editor'
    });
  };

  const getRoleBadge = (role) => {
    const badges = {
      master_admin: 'bg-secondary-100 text-secondary-800',
      admin: 'bg-green-100 text-green-800',
      editor: 'bg-blue-100 text-blue-800'
    };
    const labels = {
      master_admin: t('cms.adminManagement.masterAdmin'),
      admin: t('cms.adminManagement.admin'),
      editor: t('cms.adminManagement.editor')
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badges[role] || 'bg-gray-100 text-gray-800'}`}>
        {labels[role] || role}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading admins...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.adminManagement.title')}</h1>
          <p className="text-gray-600">{t('cms.adminManagement.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary transition-colors"
        >
          {t('cms.adminManagement.addAdmin')}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('cms.common.status')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('cms.adminManagement.lastLogin')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('cms.common.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {admins.map((admin) => (
              <tr key={admin.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {admin.avatarUrl ? (
                      <img
                        src={admin.avatarUrl}
                        alt={admin.username}
                        className="h-10 w-10 rounded-full mr-3"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary-400 to-secondary-400 flex items-center justify-center text-white font-bold mr-3">
                        {admin.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium text-gray-900">{admin.username}</div>
                      <div className="text-sm text-gray-500">{admin.fullName}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {admin.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getRoleBadge(admin.role)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {admin.isActive ? (
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                      {t('cms.adminManagement.active')}
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                      {t('cms.adminManagement.suspended')}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {admin.lastLoginAt ? new Date(admin.lastLoginAt).toLocaleDateString() : 'Never'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => handleEdit(admin)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(admin.id, admin.isActive)}
                    className={admin.isActive ? "text-yellow-600 hover:text-yellow-900" : "text-green-600 hover:text-green-900"}
                  >
                    {admin.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  {admin.role !== 'master_admin' && (
                    <button
                      onClick={() => handleDelete(admin.id, admin.username)}
                      className="text-red-600 hover:text-red-900"
                    >
                      {t('cms.common.delete')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">
              {editingAdmin ? t('cms.adminManagement.editAdmin') : t('cms.adminManagement.addAdmin')}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Username</label>
                  <input
                    type="text"
                    required
                    disabled={editingAdmin}
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    required={!editingAdmin}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingAdmin ? "Leave blank to keep current" : ""}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 border"
                  >
                    <option value="editor">Editor - Content Creation and Stats</option>
                    <option value="admin">Site Manager - Regional Administration</option>
                    <option value="master_admin">Master Admin - Platform Owner</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.role === 'editor'
                      ? 'Can manage content (challenges, users, NFTs, landing page) and view statistics'
                      : formData.role === 'admin'
                      ? 'Can access Settings and most CMS features, but not Terminal or Admin Management'
                      : 'Full system access including Terminal, Admin Management, and Blockchain settings'}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cms.common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary transition-colors"
                >
                  {t('cms.common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
